/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { BrowserSupportService } from 'browser-support/lib/browser-support.service';
import {
    AppWindowStatePayload,
    ShellMessage,
    ShellMessageType,
    VegaBridgeErrorCode,
    WindowWithVegaBridge
} from './bridge.types';

// Telemetry client type for exceptions forwarded from the Vega RN shell.
// Keep in sync with the ClientType enum in the telemetry schema.
const SHELL_CLIENT_TYPE = 'VegaShellApp';

const SHELL_LOG_SOURCE = SHELL_CLIENT_TYPE;
const SHELL_LOG_TAG = SHELL_CLIENT_TYPE;

/** Shape of an exception payload forwarded from the RN shell. Untrusted: all optional. */
interface NativeExceptionPayload {
    name?: string;
    message?: string;
    stack?: string;
    isFatal?: boolean;
}

/** Forwarded shell console log entry. */
interface ShellLogEntry {
    level?: string;
    text?: string;
}

/** Batch payload for `ShellMessageType.Log` messages from the shell. */
interface ShellLogPayload {
    source?: string;
    entries?: ShellLogEntry[];
}

/** Error carrying a telemetry client-type read by ExceptionHandlerService. */
type ClientTaggedError = Error & { clientType?: string };

/**
 * Low-level shell transport. Owns the generic `ShellMessage` channel in both
 * directions - web → shell via the static `postToShell`, and shell → web via
 * `window.onShellMessage` fanned out on `shellMessages$` - plus Vega host
 * detection. It has no knowledge of the bridge request/response protocol; that
 * lives in the bridge command layer (VegaPluginService).
 */
@Injectable({
    providedIn: 'root'
})
export class VegaHelperService {
    private readonly shellMessages = new Subject<ShellMessage>();

    /** Logger for forwarded shell logs. */
    private shellLogger: Logger | null = null;

    /** Logger for this service's own diagnostics. */
    private logger: Logger | null = null;

    constructor(private readonly injector: Injector) {
        this.installMessageHost();
    }

    /**
     * Vega host detection. Static so it can be used in non-DI contexts (module
     * load, static methods, the bridge provider factory) without constructing
     * the service - a static call runs no constructor side effects.
     *
     * The shell injects `vegaShellInfo` via `injectedJavaScriptBeforeContentLoaded`,
     * so this flag is present before the page's own scripts run - no onLoad race.
     */
    static isVegaWebViewAvailable(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        return (window as WindowWithVegaBridge).vegaShellInfo?.isVegaWebView === true;
    }

    /**
     * RN shell (.vpkg) version from `manifest.toml`, injected alongside
     * `isVegaWebView` before the page's own scripts run. Empty string when not
     * running under the Vega shell.
     */
    static getShellVersion(): string {
        if (typeof window === 'undefined') {
            return '';
        }
        const version = (window as WindowWithVegaBridge).vegaShellInfo?.version;
        return typeof version === 'string' ? version.trim() : '';
    }

    /**
     * Raw transport send (web → shell). Static so non-DI callers (e.g. the log
     * forwarder, the bridge command layer) can reuse the same channel without
     * constructing the service.
     */
    static postToShell(message: ShellMessage): void {
        if (!VegaHelperService.isVegaWebViewAvailable()) {
            throw {
                code: VegaBridgeErrorCode.Unavailable,
                message: 'Vega WebView bridge is not available'
            };
        }
        (window as WindowWithVegaBridge).ReactNativeWebView?.postMessage(JSON.stringify(message));
    }

    /** Stream of inbound shell messages; consumers filter by `messageType`. */
    get shellMessages$(): Observable<ShellMessage> {
        return this.shellMessages.asObservable();
    }

    private installMessageHost(): void {
        if (typeof window === 'undefined') {
            return;
        }
        (window as WindowWithVegaBridge).onShellMessage = (message: ShellMessage) => this.receiveShellMessage(message);
    }

    /** Transport receive: validate the envelope, fan out, or route Exception/AppWindowState messages to their handlers. */
    private receiveShellMessage(message: ShellMessage): void {
        if (!message || typeof message.messageType !== 'string') {
            return;
        }

        if (message.messageType === ShellMessageType.Exception) {
            this.reportNativeException(message.data);
        } else if (message.messageType === ShellMessageType.Log) {
            this.ingestShellLogBatch(message.data);
        } else if (message.messageType === ShellMessageType.AppWindowState) {
            this.forwardAppWindowState(message.data);
        } else {
            this.shellMessages.next(message);
        }
    }

    /**
     * Merge a batch of shell console lines into the LoggingService.
     */
    private ingestShellLogBatch(data: unknown): void {
        const payload = data as ShellLogPayload;
        if (payload?.source !== SHELL_LOG_SOURCE || !Array.isArray(payload.entries)) {
            return;
        }
        const logger = this.getShellLogger();
        for (const entry of payload.entries) {
            if (typeof entry?.level !== 'string' || typeof entry?.text !== 'string' || !entry.text) {
                continue;
            }
            switch (entry.level) {
                case 'ERROR':
                    logger.error(entry.text);
                    break;
                case 'WARNING':
                    logger.warn(entry.text);
                    break;
                default:
                    logger.info(entry.text);
                    break;
            }
        }
    }

    private getShellLogger(): Logger {
        if (!this.shellLogger) {
            this.shellLogger = this.injector.get(LoggingService).getLogger(SHELL_LOG_TAG);
        }
        return this.shellLogger;
    }

    private getLogger(): Logger {
        if (!this.logger) {
            this.logger = this.injector.get(LoggingService).getLogger('VegaHelperService');
        }
        return this.logger;
    }

    /**
     * Owns the AppWindowState protocol (validation) and pushes the resolved state
     * into BrowserSupportService, resolved lazily via the Injector to avoid a
     * construction-order dependency cycle.
     */
    private forwardAppWindowState(data: unknown): void {
        const state = (data as AppWindowStatePayload)?.state;
        if (state === 'foreground' || state === 'background') {
            console.log(`Received app window state from native shell: ${state} at ${new Date().toISOString()}`);
            this.injector.get(BrowserSupportService).notifyWindowStateChange(state);
        }
    }

    private reportNativeException(data: unknown): void {
        try {
            const ex = (data ?? {}) as NativeExceptionPayload;
            const error: ClientTaggedError = new Error(ex.message ?? '');
            error.name = ex.name ?? 'Error';
            if (ex.stack) {
                error.stack = ex.stack;
            }
            error.clientType = SHELL_CLIENT_TYPE;
            this.injector.get(ErrorHandler).handleError(error);
        } catch (err) {
            this.getLogger().error('Failed to route native exception:', err);
        }
    }
}
